class XMovingLine extends HTMLElement {
  constructor() {
    super();
    this._rafId = null;
    this._lastTs = 0;
    this._x = 0;
    this._viewport = null;
    this._track = null;
    this._originalContent = null;
    this._baseWidth = 1;
    this._resizeObserver = null;
    this._imagesListenerCleanup = null;
    this._rebuildRaf = 0;
    this._lastScrollY = window.scrollY || 0;
    this._scrollVel = 0;
    this._ready = false;
    this._io = null;

    this._onResize = this._onResize.bind(this);
    this._onScroll = this._onScroll.bind(this);
    this._tick = this._tick.bind(this);
  }

  connectedCallback() {
    const reduceMotion = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    this._originalContent = this.querySelector('[data-moving-line-element="content"]');
    if (!this._originalContent) return;

    if (!this.querySelector(".moving-line__viewport")) {
      const viewport = document.createElement("div");
      viewport.className = "moving-line__viewport";
      const track = document.createElement("div");
      track.className = "moving-line__track";
      this._originalContent.parentNode.removeChild(this._originalContent);
      track.appendChild(this._originalContent);
      viewport.appendChild(track);
      this.appendChild(viewport);
    }

    this._viewport = this.querySelector(".moving-line__viewport");
    this._track = this.querySelector(".moving-line__track");

    this._resizeObserver = new ResizeObserver(this._onResize);
    this._resizeObserver.observe(this);
    this._resizeObserver.observe(this._viewport);

    this._attachImagesLoadListeners();

    this._io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        this._rebuild(true);
        if (this._io) this._io.disconnect();
        this._io = null;
      }
    }, { root: null, threshold: 0.01 });
    this._io.observe(this);

    this._initialBuild().then(() => {
      if (reduceMotion) {
        this._setTransform(0);
        this._markReady();
        return;
      }
      window.addEventListener("scroll", this._onScroll, { passive: true });
      this._lastScrollY = window.scrollY || 0;
      this._scrollVel = 0;
      this._start();
      this._markReady();
    });
  }

  disconnectedCallback() {
    this._stop();
    window.removeEventListener("scroll", this._onScroll, { passive: true });

    if (this._resizeObserver) {
      this._resizeObserver.disconnect();
      this._resizeObserver = null;
    }

    if (this._imagesListenerCleanup) {
      this._imagesListenerCleanup();
      this._imagesListenerCleanup = null;
    }

    if (this._rebuildRaf) cancelAnimationFrame(this._rebuildRaf);

    if (this._io) {
      this._io.disconnect();
      this._io = null;
    }
  }

  _markReady() {
    if (this._ready) return;
    this._ready = true;
    this.setAttribute("data-ready", "true");
  }

  _getDirectionSign() {
    const dir = (getComputedStyle(this).getPropertyValue("--moving-line-direction") || "normal").trim();
    return dir === "reverse" ? 1 : -1;
  }

  _getSpeedMult() {
    const raw = (getComputedStyle(this).getPropertyValue("--moving-line-animation-speed-mult") || "1").trim();
    const v = parseFloat(raw);
    return Number.isFinite(v) && v > 0 ? v : 1;
  }

  _attachImagesLoadListeners() {
    const imgs = this.querySelectorAll("img");
    const handlers = [];
    const onDone = () => {
      if (this._rebuildRaf) cancelAnimationFrame(this._rebuildRaf);
      this._rebuildRaf = requestAnimationFrame(() => this._rebuild(true));
    };

    imgs.forEach((img) => {
      if (img.complete) return;
      const fn = () => onDone();
      img.addEventListener("load", fn, { passive: true });
      img.addEventListener("error", fn, { passive: true });
      handlers.push([img, fn]);
    });

    this._imagesListenerCleanup = () => {
      handlers.forEach(([img, fn]) => {
        img.removeEventListener("load", fn);
        img.removeEventListener("error", fn);
      });
    };
  }

  _waitForSomeLayout() {
    return new Promise((resolve) => {
      let tries = 0;
      const step = () => {
        tries += 1;
        const vw = this._viewport ? this._viewport.clientWidth : 0;
        const cw = this._measureBaseWidth();
        if (vw > 0 && cw > 0) return resolve();
        if (tries >= 30) return resolve();
        requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    });
  }

  async _initialBuild() {
    await this._waitForSomeLayout();
    this._rebuild(false);
  }

  _onResize() {
    this._rebuild(true);
  }

  _measureBaseWidth() {
    if (!this._originalContent) return 0;
    const r = this._originalContent.getBoundingClientRect();
    const w = Math.round(r.width);
    if (w > 0) return w;
    const sw = Math.round(this._originalContent.scrollWidth);
    return sw > 0 ? sw : 0;
  }

  _rebuild(preserveProgress) {
    if (!this._viewport || !this._track || !this._originalContent) return;

    const oldBase = this._baseWidth || 1;
    const oldX = this._x;

    const contents = Array.from(this._track.children).filter((el) => el.classList && el.classList.contains("moving-line__content"));
    if (contents.length > 1) {
      for (let i = 1; i < contents.length; i++) contents[i].remove();
    }

    this._track.style.transform = "translate3d(0px,0,0)";

    const viewportW = Math.max(1, Math.round(this._viewport.clientWidth));
    const baseW = this._measureBaseWidth();
    this._baseWidth = baseW > 0 ? baseW : 1;

    const minSets = Math.max(2, Math.ceil((viewportW * 2) / this._baseWidth) + 1);
    for (let i = 1; i < minSets; i++) {
      const clone = this._originalContent.cloneNode(true);
      clone.querySelectorAll("[id]").forEach((n) => n.removeAttribute("id"));
      this._track.appendChild(clone);
    }

    let nextX = oldX;

    if (preserveProgress && oldBase > 0 && this._baseWidth > 0) {
      const norm = this._normalizeX(oldX, oldBase);
      const progress = norm / oldBase;
      nextX = progress * this._baseWidth;
    }

    this._x = this._normalizeX(nextX, this._baseWidth);
    this._setTransform(this._x);
  }

  _normalizeX(x, baseWidth) {
    const w = baseWidth || 1;
    let v = x % w;
    if (v > 0) v -= w;
    return v;
  }

  _setTransform(x) {
    if (!this._track) return;
    this._track.style.transform = `translate3d(${x}px, 0, 0)`;
  }

  _start() {
    this._stop();
    this._lastTs = 0;
    this._rafId = requestAnimationFrame(this._tick);
  }

  _stop() {
    if (this._rafId) cancelAnimationFrame(this._rafId);
    this._rafId = null;
  }

  _onScroll() {
    if (this._baseWidth <= 1) this._rebuild(true);

    const y = window.scrollY || 0;
    const dy = y - this._lastScrollY;
    this._lastScrollY = y;

    const speedMult = this._getSpeedMult();
    this._scrollVel += dy * 0.04 * speedMult;

    const max = 12 * speedMult;
    if (this._scrollVel > max) this._scrollVel = max;
    if (this._scrollVel < -max) this._scrollVel = -max;
  }

  _tick(ts) {
    if (!this._lastTs) this._lastTs = ts;
    const dt = Math.min(40, ts - this._lastTs);
    this._lastTs = ts;

    const dirSign = this._getDirectionSign();

    if (Math.abs(this._scrollVel) > 0.001) {
      const frameFactor = dt / 16;
      const dx = this._scrollVel * dirSign * frameFactor * 0.30;
      this._x = this._normalizeX(this._x + dx, this._baseWidth);
      this._setTransform(this._x);
      this._scrollVel *= 0.90;
    }

    this._rafId = requestAnimationFrame(this._tick);
  }
}

if (!customElements.get("x-moving-line")) {
  customElements.define("x-moving-line", XMovingLine);
}